import React from "react";
import styled from "styled-components";
import { currentStatus } from "../../data/current-status";

// Phase A.5 fix: dropped the typewriter loop entirely.
//   Reasons: (a) was caught mid-animation in screenshots, looking broken;
//   (b) "still using a typewriter in 2026" is a junior-dev tell;
//   (c) a confident static value-prop reads as senior, not as gimmick.

const Title = styled.div`
  position: relative;
  width: 100%;
  text-align: center;
  padding: 0 12px;
  box-sizing: border-box;

  .hero-name {
    font-family: "Inter", "Montserrat", "Helvetica Neue", sans-serif;
    font-size: clamp(2.4rem, 6vw, 4.2rem);
    font-weight: 800;
    color: #ffffff;
    letter-spacing: -0.02em;
    line-height: 1.05;
    margin: 0 0 0.4rem 0;
  }

  .hero-name .accent {
    color: #22d3ee;
  }

  .hero-role {
    font-family: "JetBrains Mono", "Roboto Mono", monospace;
    font-size: clamp(0.85rem, 1.5vw, 1rem);
    font-weight: 500;
    letter-spacing: 0.06em;
    color: #94a3b8;
    margin: 0 0 1.4rem 0;
    text-transform: uppercase;
  }

  .hero-role .at {
    color: #475569;
    margin: 0 0.4em;
  }

  .hero-role .company {
    color: #cbd5e1;
  }

  .hero-pitch {
    font-family: "Inter", "Montserrat", sans-serif;
    font-size: clamp(1rem, 2vw, 1.25rem);
    font-weight: 400;
    line-height: 1.5;
    color: #cbd5e1;
    max-width: 720px;
    margin: 0 auto 1.8rem auto;
  }

  .hero-pitch strong {
    color: #ffffff;
    font-weight: 600;
  }

  .hero-pitch .accent {
    color: #67e8f9;
    font-weight: 600;
    border-bottom: 1px dashed rgba(34, 211, 238, 0.4);
    padding-bottom: 1px;
    text-decoration: none;
  }

  .hero-pitch .accent:hover {
    color: #22d3ee;
    border-bottom-color: #22d3ee;
  }

  .hero-cta-row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.7rem;
    justify-content: center;
    margin-top: 0.4rem;
    padding: 0 0.5rem;
  }

  .hero-cta {
    display: inline-flex;
    align-items: center;
    gap: 0.4em;
    padding: 0.7rem 1.3rem;
    border-radius: 999px;
    font-family: "Inter", "Montserrat", sans-serif;
    font-size: 0.92rem;
    font-weight: 600;
    letter-spacing: 0.01em;
    text-decoration: none;
    transition:
      background-color 0.15s ease,
      color 0.15s ease,
      border-color 0.15s ease,
      transform 0.15s ease,
      box-shadow 0.15s ease;
    cursor: pointer;
    border: 1px solid transparent;
  }

  .hero-cta-primary {
    background: #22d3ee;
    color: #06262d !important;
    border-color: #22d3ee;
    font-weight: 700;
    box-shadow: 0 4px 18px rgba(34, 211, 238, 0.28);
  }

  .hero-cta-primary:hover,
  .hero-cta-primary:focus {
    background: #67e8f9;
    border-color: #67e8f9;
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(34, 211, 238, 0.45);
    text-decoration: none;
  }

  .hero-cta-secondary {
    background: rgba(255, 255, 255, 0.04);
    color: #ffffff !important;
    border: 1px solid rgba(255, 255, 255, 0.18);
  }

  .hero-cta-secondary:hover,
  .hero-cta-secondary:focus {
    background: rgba(34, 211, 238, 0.1);
    color: #67e8f9 !important;
    border-color: #22d3ee;
    transform: translateY(-2px);
    text-decoration: none;
  }

  /* Currently strip \u2014 two stacked rows so NOW and OPEN TO each get
     visual weight rather than competing on a single dense line.
     Cyan dot signals liveness; uppercase mono labels read as terminal-y. */
  .hero-status {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 6px;
    margin: 1.8rem auto 0 auto;
    padding: 0.85rem 1.2rem;
    border-radius: 14px;
    background: rgba(15, 23, 42, 0.65);
    border: 1px solid rgba(34, 211, 238, 0.28);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    max-width: 720px;
    font-family: "JetBrains Mono", "Roboto Mono", monospace;
    font-size: 0.78rem;
    letter-spacing: 0.02em;
    color: #cbd5e1;
    text-align: left;
    box-shadow:
      0 4px 16px rgba(0, 0, 0, 0.35),
      inset 0 1px 0 rgba(255, 255, 255, 0.04);
  }

  .hero-status-dot {
    display: inline-block;
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: #22d3ee;
    box-shadow:
      0 0 12px rgba(34, 211, 238, 0.85),
      0 0 0 3px rgba(34, 211, 238, 0.18);
    animation: heroStatusPulse 1.8s ease-in-out infinite;
    flex-shrink: 0;
  }

  @keyframes heroStatusPulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.5; transform: scale(0.8); }
  }

  .hero-status-segment {
    display: flex;
    align-items: center;
    gap: 12px;
    line-height: 1.5;
  }

  .hero-status-label {
    color: #22d3ee;
    text-transform: uppercase;
    font-weight: 700;
    letter-spacing: 0.16em;
    font-size: 0.7rem;
    min-width: 64px;
    flex-shrink: 0;
    display: inline-block;
  }

  .hero-status-text {
    color: #e2e8f0;
    font-size: 0.78rem;
    line-height: 1.45;
  }

  .hero-status-divider {
    height: 1px;
    background: linear-gradient(
      to right,
      transparent 0%,
      rgba(34, 211, 238, 0.22) 50%,
      transparent 100%
    );
    width: 100%;
    margin: 2px 0;
  }

  @media screen and (max-width: 768px) {
    .hero-cta-row {
      gap: 0.5rem;
    }
    .hero-cta {
      font-size: 0.86rem;
      padding: 0.6rem 1.1rem;
    }
  }

  @media screen and (max-width: 480px) {
    .hero-status {
      font-size: 0.66rem;
      padding: 0.5rem 0.85rem;
    }
  }
`;

const TitleMessage = () => (
  <Title>
    <h1 className="hero-name">
      Dhruv <span className="accent">Agnihotri</span>
    </h1>
    <p className="hero-role">
      Lead Member of Technical Staff
      <span className="at">@</span>
      <span className="company">Salesforce</span>
    </p>
    <p className="hero-pitch">
      I build <strong>authentication for AI agents</strong>. By day I own the
      auth layer Salesforce&rsquo;s Marketing platform runs on; by night
      I&rsquo;m building <a href="#authgent" className="accent">Authgent</a>{" "}
      &mdash; the first OAuth&nbsp;2.1 server purpose-built for agentic
      delegation. Published on <a
        href="https://www.infoq.com/articles/dpop-key-storage-unsolved-problem/"
        target="_blank"
        rel="noopener noreferrer"
        className="accent"
      >InfoQ</a> on DPoP (RFC&nbsp;9449).
    </p>
    <div className="hero-cta-row">
      <a
        href="https://www.infoq.com/articles/dpop-key-storage-unsolved-problem/"
        target="_blank"
        rel="noopener noreferrer"
        className="hero-cta hero-cta-primary"
      >
        Read on InfoQ {"\u2197"}
      </a>
      <a
        href="/Resume_Dhruv_Agnihotri.pdf"
        target="_blank"
        rel="noopener noreferrer"
        className="hero-cta hero-cta-secondary"
      >
        Resume (PDF)
      </a>
      <a
        href="https://github.com/authgent/authgent"
        target="_blank"
        rel="noopener noreferrer"
        className="hero-cta hero-cta-secondary"
      >
        Authgent on GitHub {"\u2197"}
      </a>
    </div>
    <div className="hero-status" role="status" aria-label="Current status">
      <div className="hero-status-segment">
        <span className="hero-status-dot" aria-hidden="true" />
        <span className="hero-status-label">Now</span>
        <span className="hero-status-text">{currentStatus.working}</span>
      </div>
      <div className="hero-status-divider" aria-hidden="true" />
      <div className="hero-status-segment">
        <span
          className="hero-status-dot"
          style={{ background: "#a78bfa", boxShadow: "0 0 12px rgba(167,139,250,0.85), 0 0 0 3px rgba(167,139,250,0.18)" }}
          aria-hidden="true"
        />
        <span className="hero-status-label">Latest</span>
        <span className="hero-status-text">{currentStatus.lastShipped}</span>
      </div>
    </div>
  </Title>
);

export default TitleMessage;
